import type { EntryPoint, EntryPointComponent } from 'react-relay';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
/* eslint-disable eslint-comments/no-restricted-disable */
/* eslint-disable @atlassian/react-entrypoint/no-imports-in-entrypoint-file */
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { getProjectKeyId } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import parameters from './ui/__generated__/HorizontalNavJiraInnerQuery$parameters';
import type { HorizontalNavJiraInnerQuery } from './ui/__generated__/HorizontalNavJiraInnerQuery.graphql';

const NAVIGATION_ITEMS_PAGE_SIZE = 75;

type JiraHorizontalNavEntrypointComponent = EntryPointComponent<
	{
		horizontalNavQuery: HorizontalNavJiraInnerQuery;
	},
	{},
	{},
	{}
>;

export const jiraHorizontalNavEntrypoint: EntryPoint<
	JiraHorizontalNavEntrypointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<JiraHorizontalNavEntrypointComponent>(
		/* webpackChunkName: "async-jira-horizontal-nav-entrypoint" */ './ui/HorizontalNavJiraInner.tsx',
		{
			entryFsSsrLiftImportToModule: true,
			includeInShellOnlySsrBundle: true,
		},
	),
	getPreloadProps: ({
		context: { match, query },
		tenantContext: { cloudId },
	}: EntryPointRouteParams) => {
		let boardId = match.params.boardId;
		const { projectKey, projectId } = getProjectKeyId(match, query);
		const projectIdOrKey = projectId || projectKey;
		if (!boardId) {
			// If there is no boardId in the URL then we should check to see if it is provided as part of
			// the Connect app rapidView query param. This has been added to address JRACLOUD-94629
			const rapidViewBoard = query.rapidView ? query.rapidView : null;
			boardId = rapidViewBoard || boardId || null;
		}
		const boardAri = boardId ? `ari:cloud:jira-software:${cloudId}:board/${boardId}` : '';
		const projectAri = projectId
			? JiraProjectAri.create({
					siteId: cloudId,
					projectId,
				}).toString()
			: '';
		const containerNavigationInput =
			boardAri || projectAri
				? { scopeId: boardAri || projectAri }
				: { projectKeyQuery: { cloudId, projectKey } };
		// mostRecentlyViewedBoard is only ever rendered as a fallback for company-managed (CMP)
		// software project routes that don't have a board in the URL (the nav header discards it for
		// TMP and whenever a board ARI is known). CMP routes carry a /software/c/ segment in the URL,
		// while TMP routes use /software/projects/; only the CMP board view loses its /c prefix during
		// URL harmonization, and that route always has a board id, so it is still skipped.
		// We only request mostRecentlyViewedBoard for boardless CMP routes.
		const isCMPRoute = match.url.includes('/software/c/');
		const skipMostRecentlyViewedBoard = !(isCMPRoute && !boardAri);
		return {
			queries: {
				horizontalNavQuery: {
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
					parameters,
					variables: {
						cloudId,
						projectIdOrKey,
						withProject: Boolean(projectIdOrKey),
						boardId: boardAri,
						withBoard: Boolean(boardAri),
						containerNavigationInput,
						skipMostRecentlyViewedBoard,
						first: NAVIGATION_ITEMS_PAGE_SIZE,
					},
				},
			},
		};
	},
});
